import useSwapData from '@/application/read/swap/useSwapData';
import useSwapState from '@/application/read/swap/useSwapState';
import { SwapState } from '@/domain/swap';
import { useEffect, useMemo, useState } from 'react';
import { Tab } from '../types';
import useVaults from '@/application/read/vaults/useVaults';
import { Address, Asset, TokenId } from 'nftx.js';
import { QueryStatus } from '@tanstack/react-query';
import getWeakestStatus from '@/presentation/utils/getWeakestStatus';
import SwapSearch from './SwapSearch';
import Popular from './modules/Popular';
import Collection from './modules/Collection';
import Nft from './modules/Nft';
import VaultToken from './modules/VaultToken';
import All from './sections/All';
import Nfts from './sections/Nfts';
import Tokens from './sections/Tokens';
import {
  useAssets,
  useCollections,
  usePopularVaults,
  useVisible,
  useVtokens,
} from './hooks';
import useTraitFilters from '@/presentation/hooks/filters/useTraitFilters';

type SwapDispatch = ReturnType<typeof useSwapState>[1];

type Props = SwapState & {
  userAddress: Address | undefined;
  tab: Tab;
  onTabChange: (v: Tab) => void;
  search: string;
  onSearch: (v: string) => void;
  limit: number;
  onShowMore: () => void;
  searchedVaultIds: string[];
  popularVaultIds: string[] | null;
  dispatch: SwapDispatch;
  ethBalance?: bigint;
};

const ASSET_PAGE_SIZE = 18;

const BuySearchContainer = ({
  userAddress,
  tab,
  onTabChange,
  popularVaultIds,
  dispatch,
  search,
  onSearch,
  limit,
  onShowMore,
  searchedVaultIds,
  ethBalance,
  ...initialState
}: Props) => {
  const assetLimit = limit;

  let [state, dispatchLocal] = useSwapState({
    ...initialState,
    amountController: 'buy',
  });

  // Always use the given controller state
  state = {
    ...state,
    filterController: initialState.filterController,
    amountController: 'buy',
  };
  const data = useSwapData({ userAddress, ...state });
  const { data: allVaults } = useVaults();

  const vaultId = state.buyVaultId;

  const showNfts = state.buyType === 'NFT' && !!state.buyToken;

  const popularVaults = usePopularVaults(allVaults, popularVaultIds);

  const {
    canFilter,
    filteredAssets = [],
    filters,
    setTraitFilters,
    toggleTraitFilter,
  } = useTraitFilters({ assets: data.redeemableAssets });

  useEffect(() => {
    setTraitFilters({});
  }, [setTraitFilters, state.sellVaultId]);

  const vTokens = useVtokens(data.redeemableVTokens, search, searchedVaultIds);

  const collections = useCollections(
    data.redeemableCollections,
    search,
    searchedVaultIds
  );

  const assets = useAssets({
    assets: filteredAssets,
    search,
    searchedVaultIds,
    vaultId,
  });

  const {
    visibleAssets,
    visibleCollections,
    visibleVTokens,
    hasMoreCollections,
    hasMoreNfts,
    hasMoreTokens,
  } = useVisible({
    vTokens: vTokens?.sort((a, b) => Number(b.balance - a.balance)),
    assets,
    collections,
    limit,
    assetLimit,
  });

  // own internal state to dropdown and show vaults
  const handleVaultClick = (assetAddress: Address, newVaultId: string) => {
    const vault = allVaults?.find((v) => v.vaultId === newVaultId);
    if (!vault) {
      return;
    }

    // switch to nft view
    onTabChange('nfts');

    dispatchLocal({
      type: 'buy_nfts',
      assetAddress: assetAddress,
      vaultAddress: vault.id,
      items: [],
      vaultId: newVaultId,
    });
  };

  const handleBackClick = () => {
    dispatchLocal({
      type: 'buy_nfts',
      vaultId: '',
      assetAddress: '' as Address,
      vaultAddress: '' as Address,
      items: [],
    });
  };

  const handleChooseEth = () => {
    dispatch({ type: 'buy_eth' });
  };
  const handleChooseVToken = (vaultAddress: Address, vaultId: string) => {
    dispatch({ type: 'buy_vtoken', vaultAddress, vaultId });
  };
  const handleConfirmTokenIds = () => {
    if (state.buyToken && state.buyItems.length) {
      dispatch({
        type: 'buy_nfts',
        vaultId: state.buyVaultId,
        assetAddress: state.buyToken,
        vaultAddress: state.buyVaultAddress as Address,
        items: state.buyItems,
      });
    } else {
      dispatch({
        type: 'buy_nfts',
        vaultId: '',
        assetAddress: '' as Address,
        vaultAddress: '' as Address,
        items: [],
      });
    }
  };

  const vault = allVaults?.find((v) => v.vaultId === vaultId);

  const handleAssetClick = (asset: Asset) => {
    const newItems = [...state.buyItems];
    const item = newItems.find(([tokenId]) => tokenId === asset.tokenId);

    if (item) {
      const i = newItems.indexOf(item);
      newItems.splice(i, 1);
    } else {
      newItems.push([asset.tokenId, 1]);
    }

    dispatchLocal({
      type: 'buy_nfts',
      vaultId: state.buyVaultId,
      assetAddress: asset.assetAddress,
      vaultAddress: state.buyVaultAddress as Address,
      items: newItems,
    });
  };

  const status = useMemo(() => {
    const statuses: QueryStatus[] = [];

    if (tab === 'tokens' || tab === 'all') {
      // showing vToken data from vaults
      statuses.push(data.vaultsStatus);
      if (userAddress) {
        // if wallet is connected we also want to wait for your balances to load (for sorting)
        statuses.push(data.balancesStatus);
      }
    }
    if (tab === 'nfts' || tab === 'all') {
      // showing NFT collections sourced from vaults
      statuses.push(data.vaultsStatus);
      if (showNfts) {
        // showing individual NFTs from a collection
        statuses.push(data.redeemableAssetsStatus);
      }
    }

    return getWeakestStatus(...statuses);
  }, [
    data.balancesStatus,
    data.redeemableAssetsStatus,
    data.vaultsStatus,
    showNfts,
    tab,
    userAddress,
  ]);

  const renderCollections = visibleCollections.map((c) => (
    <Collection
      key={c.id}
      {...c}
      token={initialState.buyToken}
      type="buy"
      collectionAssets={[]}
      onClick={(vaultId) => handleVaultClick(c.id, vaultId)}
    />
  ));

  const renderPopularCollections = popularVaults.map((vault) => (
    <Popular
      key={vault.vaultId}
      vault={vault}
      type="collection"
      onClick={() => handleVaultClick(vault.asset.id, vault.vaultId)}
    />
  ));

  const renderPopularTokens = [
    <Popular key="eth" type="eth" onClick={() => handleChooseEth()} />,
    ...popularVaults.map((vault) => (
      <Popular
        key={vault.vaultId}
        vault={vault}
        type="token"
        onClick={() => handleChooseVToken(vault.id, vault.vaultId)}
      />
    )),
  ];

  const renderTokens = visibleVTokens.map((v) => (
    <VaultToken
      key={v.id}
      {...v}
      token={state.buyToken}
      onClick={() => handleChooseVToken(v.id, v.vaultId)}
    />
  ));

  const onChangeQuantity = (
    value: number,
    asset: Pick<Asset, 'tokenId' | 'quantity' | 'assetAddress'>
  ) => {
    value = Math.max(0, Math.min(value, Number(asset.quantity ?? 0n)));

    const newItems = [...state.buyItems];
    const item = newItems.find(([tokenId]) => tokenId === asset.tokenId);
    if (item && value > 0) {
      newItems[newItems.indexOf(item)] = [asset.tokenId, value];
    } else if (item && value === 0) {
      const i = newItems.indexOf(item);
      newItems.splice(i, 1);
    } else {
      newItems.push([asset.tokenId, value]);
    }

    dispatchLocal({
      type: 'buy_nfts',
      vaultId: state.buyVaultId,
      assetAddress: asset.assetAddress,
      vaultAddress: state.buyVaultAddress as Address,
      items: newItems,
    });
  };

  const renderNfts = visibleAssets.map((asset) => {
    const item = state.buyItems.find(([tokenId]) => tokenId === asset.tokenId);

    return (
      <Nft
        key={asset.id}
        {...asset}
        selected={!!item}
        onClick={() => handleAssetClick(asset)}
        is1155={!!vault?.is1155}
        maxQuantity={Number(asset.quantity ?? 0n)}
        quantity={item?.[1] ?? 0}
        onChangeQuantity={(value) => {
          onChangeQuantity(value, asset);
        }}
      />
    );
  });

  return (
    <SwapSearch
      tabs={[
        <All
          key="all"
          collections={renderCollections}
          hasMoreCollections={hasMoreCollections}
          hasMoreTokens={hasMoreTokens}
          onMoreClick={onShowMore}
          popularCollections={renderPopularCollections}
          popularTokens={renderPopularTokens}
          status={status}
          token={state.buyToken}
          tokens={renderTokens}
          isConnected={!!userAddress}
        />,
        <Nfts
          key="nfts"
          canFilter={canFilter}
          collections={renderCollections}
          currencySymbol={data?.sellSymbol}
          hasMoreNfts={hasMoreNfts}
          hasMoreCollections={hasMoreCollections}
          nfts={renderNfts}
          onBackClick={handleBackClick}
          onConfirmTokenIds={handleConfirmTokenIds}
          onMoreClick={onShowMore}
          popularCollections={renderPopularCollections}
          priceLoading={data.sellAmountLoading}
          selectedItems={state.buyItems}
          showNfts={showNfts}
          status={status}
          token={state.buyToken}
          totalNfts={assets.length}
          totalPrice={data.sellAmount}
          vault={vault}
          filters={filters}
          isConnected={!!userAddress}
          onFilter={toggleTraitFilter}
        />,
        <Tokens
          key="tokens"
          hasMoreTokens={hasMoreTokens}
          onChooseEth={handleChooseEth}
          onMoreClick={onShowMore}
          popularTokens={renderPopularTokens}
          status={status}
          tokens={renderTokens}
          type={state.buyType}
          ethBalance={ethBalance}
        />,
      ]}
      search={search}
      tab={tab}
      onSearch={onSearch}
      onTabChange={onTabChange}
    />
  );
};

export default BuySearchContainer;
